// Multi-theme Glassmorphism Color Palettes
// Inspired by TidyFlux's elegant aesthetic

export type ThemeName = 'default' | 'forest' | 'ocean' | 'lavender' | 'mono' | 'glass';

// ============================================================
// DEFAULT THEME (Warm Burgundy)
// ============================================================

export const lightPalette = {
  primary: {
    main: '#8b2c3b',
    light: '#a84855',
    dark: '#6b1f2d',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#6e665c',
    light: '#8a7f73',
    dark: '#564e46',
    contrastText: '#FFFFFF',
  },
  tertiary: {
    main: '#9d6b4f',
    light: '#b8856a',
    dark: '#7d533d',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#c84848',
    light: '#d97070',
    dark: '#a03030',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#d68548',
    light: '#e5a472',
    dark: '#b86a30',
    contrastText: '#000000',
  },
  info: {
    main: '#5a7a8a',
    light: '#7a95a3',
    dark: '#445c6a',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#5a7a5a',
    light: '#7a957a',
    dark: '#445c44',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#f7f3ed',
    paper: 'rgba(255, 255, 255, 1)',
  },
  text: {
    primary: '#2b2622',
    secondary: '#6e665c',
    disabled: 'rgba(43, 38, 34, 0.38)',
  },
  divider: 'rgba(43, 38, 34, 0.08)',
  action: {
    active: '#8b2c3b',
    hover: 'rgba(139, 44, 59, 0.04)',
    selected: 'rgba(139, 44, 59, 0.08)',
    disabled: 'rgba(43, 38, 34, 0.26)',
    disabledBackground: 'rgba(43, 38, 34, 0.12)',
  },
};

export const darkPalette = {
  primary: {
    main: '#e07878',
    light: '#ec9c9c',
    dark: '#c45a5a',
    contrastText: '#1a1816',
  },
  secondary: {
    main: '#a89f93',
    light: '#c4bbb0',
    dark: '#8a7f73',
    contrastText: '#1a1816',
  },
  tertiary: {
    main: '#d9a688',
    light: '#e8c4af',
    dark: '#c18660',
    contrastText: '#1a1816',
  },
  error: {
    main: '#e89090',
    light: '#f0b0b0',
    dark: '#d06868',
    contrastText: '#1a1816',
  },
  warning: {
    main: '#e8b68a',
    light: '#f0d0b0',
    dark: '#d09660',
    contrastText: '#1a1816',
  },
  info: {
    main: '#8aa8b8',
    light: '#afc4d0',
    dark: '#6a8898',
    contrastText: '#1a1816',
  },
  success: {
    main: '#90a890',
    light: '#b0c4b0',
    dark: '#708870',
    contrastText: '#1a1816',
  },
  background: {
    default: '#1a1816',
    paper: 'rgba(35, 32, 28, 1)',
  },
  text: {
    primary: '#f7f3ee',
    secondary: '#a89f93',
    disabled: 'rgba(247, 243, 238, 0.38)',
  },
  divider: 'rgba(247, 243, 238, 0.08)',
  action: {
    active: '#e07878',
    hover: 'rgba(224, 120, 120, 0.08)',
    selected: 'rgba(224, 120, 120, 0.12)',
    disabled: 'rgba(247, 243, 238, 0.3)',
    disabledBackground: 'rgba(247, 243, 238, 0.12)',
  },
};

// ============================================================
// FOREST THEME
// ============================================================

const forestLightPalette: typeof lightPalette = {
  primary: {
    main: '#355F2E',
    light: '#4d7a45',
    dark: '#264722',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#6a756e',
    light: '#85908a',
    dark: '#525c56',
    contrastText: '#FFFFFF',
  },
  tertiary: {
    main: '#6b7f3a',
    light: '#8a9e58',
    dark: '#536230',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#b84c4c',
    light: '#cc7070',
    dark: '#963838',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#c08030',
    light: '#d4a060',
    dark: '#a06820',
    contrastText: '#000000',
  },
  info: {
    main: '#4a7a6a',
    light: '#6a9a8a',
    dark: '#386050',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#3d7a3d',
    light: '#5a955a',
    dark: '#2c602c',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#f2f4f2',
    paper: 'rgba(255, 255, 255, 1)',
  },
  text: {
    primary: '#333b36',
    secondary: '#6a756e',
    disabled: 'rgba(51, 59, 54, 0.38)',
  },
  divider: 'rgba(51, 59, 54, 0.08)',
  action: {
    active: '#355F2E',
    hover: 'rgba(53, 95, 46, 0.04)',
    selected: 'rgba(53, 95, 46, 0.08)',
    disabled: 'rgba(51, 59, 54, 0.26)',
    disabledBackground: 'rgba(51, 59, 54, 0.12)',
  },
};

const forestDarkPalette: typeof darkPalette = {
  primary: {
    main: '#7aaa7a',
    light: '#9ec49e',
    dark: '#5c8c5c',
    contrastText: '#1a1d1b',
  },
  secondary: {
    main: '#919a95',
    light: '#adb5b0',
    dark: '#747d78',
    contrastText: '#1a1d1b',
  },
  tertiary: {
    main: '#a0b870',
    light: '#b8cc90',
    dark: '#889e58',
    contrastText: '#1a1d1b',
  },
  error: {
    main: '#d88888',
    light: '#e8a8a8',
    dark: '#c06868',
    contrastText: '#1a1d1b',
  },
  warning: {
    main: '#d8b070',
    light: '#e8cc98',
    dark: '#c09848',
    contrastText: '#1a1d1b',
  },
  info: {
    main: '#78b0a0',
    light: '#98c8b8',
    dark: '#5c9888',
    contrastText: '#1a1d1b',
  },
  success: {
    main: '#80b880',
    light: '#a0d0a0',
    dark: '#609860',
    contrastText: '#1a1d1b',
  },
  background: {
    default: '#1a1d1b',
    paper: 'rgba(34, 38, 35, 1)',
  },
  text: {
    primary: '#dde2df',
    secondary: '#919a95',
    disabled: 'rgba(221, 226, 223, 0.38)',
  },
  divider: 'rgba(221, 226, 223, 0.08)',
  action: {
    active: '#7aaa7a',
    hover: 'rgba(122, 170, 122, 0.08)',
    selected: 'rgba(122, 170, 122, 0.12)',
    disabled: 'rgba(221, 226, 223, 0.3)',
    disabledBackground: 'rgba(221, 226, 223, 0.12)',
  },
};

// ============================================================
// OCEAN THEME
// ============================================================

const oceanLightPalette: typeof lightPalette = {
  primary: {
    main: '#133E87',
    light: '#2a5aa0',
    dark: '#0d2e68',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#6a7580',
    light: '#8590a0',
    dark: '#525c66',
    contrastText: '#FFFFFF',
  },
  tertiary: {
    main: '#3a6a8a',
    light: '#5588a8',
    dark: '#28506c',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#c04848',
    light: '#d47070',
    dark: '#a03030',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#c08840',
    light: '#d4a868',
    dark: '#a07028',
    contrastText: '#000000',
  },
  info: {
    main: '#2868a8',
    light: '#4888c0',
    dark: '#1c5088',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#3a7a5a',
    light: '#5a9a7a',
    dark: '#2a6044',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#f2f4f6',
    paper: 'rgba(255, 255, 255, 1)',
  },
  text: {
    primary: '#333a42',
    secondary: '#6a7580',
    disabled: 'rgba(51, 58, 66, 0.38)',
  },
  divider: 'rgba(51, 58, 66, 0.08)',
  action: {
    active: '#133E87',
    hover: 'rgba(19, 62, 135, 0.04)',
    selected: 'rgba(19, 62, 135, 0.08)',
    disabled: 'rgba(51, 58, 66, 0.26)',
    disabledBackground: 'rgba(51, 58, 66, 0.12)',
  },
};

const oceanDarkPalette: typeof darkPalette = {
  primary: {
    main: '#608BC1',
    light: '#80a8d8',
    dark: '#4870a0',
    contrastText: '#16181c',
  },
  secondary: {
    main: '#909aa5',
    light: '#adb5c0',
    dark: '#747e88',
    contrastText: '#16181c',
  },
  tertiary: {
    main: '#68a0c8',
    light: '#88b8d8',
    dark: '#5088b0',
    contrastText: '#16181c',
  },
  error: {
    main: '#d88888',
    light: '#e8a8a8',
    dark: '#c06868',
    contrastText: '#16181c',
  },
  warning: {
    main: '#d8b878',
    light: '#e8d0a0',
    dark: '#c0a058',
    contrastText: '#16181c',
  },
  info: {
    main: '#60a0d0',
    light: '#80b8e0',
    dark: '#4888b8',
    contrastText: '#16181c',
  },
  success: {
    main: '#70b090',
    light: '#90c8a8',
    dark: '#589878',
    contrastText: '#16181c',
  },
  background: {
    default: '#16181c',
    paper: 'rgba(28, 32, 38, 1)',
  },
  text: {
    primary: '#dce1e8',
    secondary: '#909aa5',
    disabled: 'rgba(220, 225, 232, 0.38)',
  },
  divider: 'rgba(220, 225, 232, 0.08)',
  action: {
    active: '#608BC1',
    hover: 'rgba(96, 139, 193, 0.08)',
    selected: 'rgba(96, 139, 193, 0.12)',
    disabled: 'rgba(220, 225, 232, 0.3)',
    disabledBackground: 'rgba(220, 225, 232, 0.12)',
  },
};

// ============================================================
// LAVENDER THEME
// ============================================================

const lavenderLightPalette: typeof lightPalette = {
  primary: {
    main: '#52357B',
    light: '#6d4e96',
    dark: '#3e2860',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#747080',
    light: '#908ca0',
    dark: '#5a5666',
    contrastText: '#FFFFFF',
  },
  tertiary: {
    main: '#7a4a8a',
    light: '#9868a8',
    dark: '#603870',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#b84858',
    light: '#cc6878',
    dark: '#983040',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#c08048',
    light: '#d4a070',
    dark: '#a06830',
    contrastText: '#000000',
  },
  info: {
    main: '#5868a0',
    light: '#7888b8',
    dark: '#405088',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#4a7a5a',
    light: '#6a9a7a',
    dark: '#386044',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#f4f2f6',
    paper: 'rgba(255, 255, 255, 1)',
  },
  text: {
    primary: '#38343e',
    secondary: '#747080',
    disabled: 'rgba(56, 52, 62, 0.38)',
  },
  divider: 'rgba(56, 52, 62, 0.08)',
  action: {
    active: '#52357B',
    hover: 'rgba(82, 53, 123, 0.04)',
    selected: 'rgba(82, 53, 123, 0.08)',
    disabled: 'rgba(56, 52, 62, 0.26)',
    disabledBackground: 'rgba(56, 52, 62, 0.12)',
  },
};

const lavenderDarkPalette: typeof darkPalette = {
  primary: {
    main: '#C3ACD0',
    light: '#d8c8e0',
    dark: '#a890b8',
    contrastText: '#18171c',
  },
  secondary: {
    main: '#9895a8',
    light: '#b0adc0',
    dark: '#7c7890',
    contrastText: '#18171c',
  },
  tertiary: {
    main: '#b890c8',
    light: '#d0a8d8',
    dark: '#a078b0',
    contrastText: '#18171c',
  },
  error: {
    main: '#d88890',
    light: '#e8a8b0',
    dark: '#c06870',
    contrastText: '#18171c',
  },
  warning: {
    main: '#d8b880',
    light: '#e8d0a0',
    dark: '#c0a060',
    contrastText: '#18171c',
  },
  info: {
    main: '#8898c8',
    light: '#a0b0d8',
    dark: '#6878b0',
    contrastText: '#18171c',
  },
  success: {
    main: '#80b090',
    light: '#a0c8a8',
    dark: '#609878',
    contrastText: '#18171c',
  },
  background: {
    default: '#18171c',
    paper: 'rgba(32, 30, 38, 1)',
  },
  text: {
    primary: '#e0dde6',
    secondary: '#9895a8',
    disabled: 'rgba(224, 221, 230, 0.38)',
  },
  divider: 'rgba(224, 221, 230, 0.08)',
  action: {
    active: '#C3ACD0',
    hover: 'rgba(195, 172, 208, 0.08)',
    selected: 'rgba(195, 172, 208, 0.12)',
    disabled: 'rgba(224, 221, 230, 0.3)',
    disabledBackground: 'rgba(224, 221, 230, 0.12)',
  },
};

// ============================================================
// MONO THEME
// ============================================================

const monoLightPalette: typeof lightPalette = {
  primary: {
    main: '#444444',
    light: '#666666',
    dark: '#2a2a2a',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#666666',
    light: '#888888',
    dark: '#4a4a4a',
    contrastText: '#FFFFFF',
  },
  tertiary: {
    main: '#555555',
    light: '#777777',
    dark: '#3a3a3a',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#a04040',
    light: '#b86060',
    dark: '#882828',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#8a7030',
    light: '#a89050',
    dark: '#6e5820',
    contrastText: '#000000',
  },
  info: {
    main: '#505050',
    light: '#707070',
    dark: '#383838',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#407040',
    light: '#609060',
    dark: '#285828',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#f5f5f5',
    paper: 'rgba(255, 255, 255, 1)',
  },
  text: {
    primary: '#1a1a1a',
    secondary: '#666666',
    disabled: 'rgba(26, 26, 26, 0.38)',
  },
  divider: 'rgba(26, 26, 26, 0.08)',
  action: {
    active: '#444444',
    hover: 'rgba(68, 68, 68, 0.04)',
    selected: 'rgba(68, 68, 68, 0.08)',
    disabled: 'rgba(26, 26, 26, 0.26)',
    disabledBackground: 'rgba(26, 26, 26, 0.12)',
  },
};

const monoDarkPalette: typeof darkPalette = {
  primary: {
    main: '#bbbbbb',
    light: '#d8d8d8',
    dark: '#999999',
    contrastText: '#181818',
  },
  secondary: {
    main: '#999999',
    light: '#b0b0b0',
    dark: '#7a7a7a',
    contrastText: '#181818',
  },
  tertiary: {
    main: '#aaaaaa',
    light: '#c0c0c0',
    dark: '#888888',
    contrastText: '#181818',
  },
  error: {
    main: '#c88080',
    light: '#d8a0a0',
    dark: '#b06060',
    contrastText: '#181818',
  },
  warning: {
    main: '#c0b070',
    light: '#d0c890',
    dark: '#a89850',
    contrastText: '#181818',
  },
  info: {
    main: '#a0a0a0',
    light: '#b8b8b8',
    dark: '#888888',
    contrastText: '#181818',
  },
  success: {
    main: '#88b088',
    light: '#a0c8a0',
    dark: '#709870',
    contrastText: '#181818',
  },
  background: {
    default: '#181818',
    paper: 'rgba(32, 32, 32, 1)',
  },
  text: {
    primary: '#e5e5e5',
    secondary: '#999999',
    disabled: 'rgba(229, 229, 229, 0.38)',
  },
  divider: 'rgba(229, 229, 229, 0.08)',
  action: {
    active: '#bbbbbb',
    hover: 'rgba(187, 187, 187, 0.08)',
    selected: 'rgba(187, 187, 187, 0.12)',
    disabled: 'rgba(229, 229, 229, 0.3)',
    disabledBackground: 'rgba(229, 229, 229, 0.12)',
  },
};

// ============================================================
// GLASS THEME (Dark Glassmorphism — neutral + indigo accent)
// ============================================================

const glassLightPalette: typeof lightPalette = {
  primary: {
    main: '#6366F1',
    light: '#818CF8',
    dark: '#4F46E5',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#6B7280',
    light: '#9CA3AF',
    dark: '#4B5563',
    contrastText: '#FFFFFF',
  },
  tertiary: {
    main: '#8B5CF6',
    light: '#A78BFA',
    dark: '#7C3AED',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#F59E0B',
    light: '#FCD34D',
    dark: '#D97706',
    contrastText: '#000000',
  },
  info: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F5F5F5',
    paper: 'rgba(255, 255, 255, 1)',
  },
  text: {
    primary: '#0A0A0A',
    secondary: '#4A4A4A',
    disabled: 'rgba(10, 10, 10, 0.38)',
  },
  divider: 'rgba(10, 10, 10, 0.08)',
  action: {
    active: '#6366F1',
    hover: 'rgba(99, 102, 241, 0.06)',
    selected: 'rgba(99, 102, 241, 0.12)',
    disabled: 'rgba(10, 10, 10, 0.26)',
    disabledBackground: 'rgba(10, 10, 10, 0.12)',
  },
};

const glassDarkPalette: typeof darkPalette = {
  primary: {
    main: '#818CF8',
    light: '#A5B4FC',
    dark: '#6366F1',
    contrastText: '#0A0A0A',
  },
  secondary: {
    main: '#9CA3AF',
    light: '#D1D5DB',
    dark: '#6B7280',
    contrastText: '#0A0A0A',
  },
  tertiary: {
    main: '#A78BFA',
    light: '#C4B5FD',
    dark: '#8B5CF6',
    contrastText: '#0A0A0A',
  },
  error: {
    main: '#F87171',
    light: '#FCA5A5',
    dark: '#EF4444',
    contrastText: '#0A0A0A',
  },
  warning: {
    main: '#FCD34D',
    light: '#FDE68A',
    dark: '#F59E0B',
    contrastText: '#0A0A0A',
  },
  info: {
    main: '#60A5FA',
    light: '#93C5FD',
    dark: '#3B82F6',
    contrastText: '#0A0A0A',
  },
  success: {
    main: '#34D399',
    light: '#6EE7B7',
    dark: '#10B981',
    contrastText: '#0A0A0A',
  },
  background: {
    default: '#0A0A0A',
    paper: '#18181b',
  },
  text: {
    primary: '#F5F5F5',
    secondary: '#A8A8A8',
    disabled: 'rgba(245, 245, 245, 0.38)',
  },
  divider: 'rgba(245, 245, 245, 0.08)',
  action: {
    active: '#818CF8',
    hover: 'rgba(129, 140, 248, 0.08)',
    selected: 'rgba(129, 140, 248, 0.14)',
    disabled: 'rgba(245, 245, 245, 0.30)',
    disabledBackground: 'rgba(245, 245, 245, 0.12)',
  },
};

// ============================================================
// THEME MAP
// ============================================================

export const themes: Record<ThemeName, { light: typeof lightPalette; dark: typeof darkPalette }> = {
  default: { light: lightPalette, dark: darkPalette },
  forest: { light: forestLightPalette, dark: forestDarkPalette },
  ocean: { light: oceanLightPalette, dark: oceanDarkPalette },
  lavender: { light: lavenderLightPalette, dark: lavenderDarkPalette },
  mono: { light: monoLightPalette, dark: monoDarkPalette },
  glass: { light: glassLightPalette, dark: glassDarkPalette },
};

// Accent colors for theme picker display
export const themeAccentColors: Record<ThemeName, { light: string; dark: string }> = {
  default: { light: '#8b2c3b', dark: '#e07878' },
  forest: { light: '#355F2E', dark: '#7aaa7a' },
  ocean: { light: '#133E87', dark: '#608BC1' },
  lavender: { light: '#52357B', dark: '#C3ACD0' },
  mono: { light: '#444444', dark: '#bbbbbb' },
  glass: { light: '#6366F1', dark: '#818CF8' },
};
